"use client";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useResetPassword } from "../context/ResetPasswordContext";
 
function ChangePassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const { resetPassword } = useResetPassword();
 
  const [formData, setFormData] = React.useState({
    newPassword: "",
    confirmPassword: "",
  });
 
  const [errors, setErrors] = React.useState({
    newPassword: "",
    confirmPassword: "",
  });
 
  const [showModal, setShowModal] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
 
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
 
  const validateForm = () => {
    const newErrors = { newPassword: "", confirmPassword: "" };
 
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters long";
    } else if (!passwordRegex.test(formData.newPassword)) {
      newErrors.newPassword =
        "Password must include uppercase, lowercase, and a number";
    }
 
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
 
    setErrors(newErrors);
    return !newErrors.newPassword && !newErrors.confirmPassword;
  };
 
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
 
    setIsSubmitting(true);
    const response = await resetPassword({
      email,
      token,
      password: formData.newPassword,
    });
 
    setIsSubmitting(false);
 
    if (response.status === 200) {
      setShowModal(true);
      setFormData({ newPassword: "", confirmPassword: "" });
    } else {
      setErrors({
        newPassword: response.message || "Something went wrong",
        confirmPassword: "",
      });
    }
  };
 
  const handleReturnToLogin = () => navigate("/login");
  const handleBackToStore = () => navigate("/");
  const closeModal = () => {
    setShowModal(false);
    navigate("/login");
  };
 
  // Animation variants for underline
  const underlineVariants = {
    initial: { scaleX: 0 },
    hover: {
      scaleX: 1,
      transition: { duration: 0.3, ease: "easeInOut" },
    },
  };
 
  const AnimatedLink = ({ children, onClick, className = "" }) => (
    <motion.button
      onClick={onClick}
      className={`relative text-yellow-500 bg-transparent border-none cursor-pointer p-0 outline-none ${className}`}
      whileHover="hover"
      initial="initial"
      animate="initial"
    >
      <span className="relative z-10">{children}</span>
      <motion.span
        className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 origin-center"
        variants={underlineVariants}
      />
    </motion.button>
  );
 
  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="flex flex-col gap-10 p-10 rounded-3xl shadow-lg w-full max-w-md">
          <header className="text-center">
            <h1 className="text-4xl font-bold text-yellow-500">
              Change Password
            </h1>
            <p className="text-gray-600 mt-2 text-sm">
              Update your password to continue securely
            </p>
          </header>
 
          <div className="flex justify-center gap-2 text-gray-700 text-sm">
            <span>Remember your password?</span>
            <AnimatedLink onClick={handleReturnToLogin}>
              Back to Login
            </AnimatedLink>
          </div>
 
          <form onSubmit={handleSubmit} className="flex flex-col gap-5 w-full">
            {/* New Password */}
            <div className="flex flex-col">
              <input
                type="password"
                id="newPassword"
                placeholder="New Password"
                value={formData.newPassword}
                onChange={(e) =>
                  handleInputChange("newPassword", e.target.value)
                }
                className={`px-4 py-3 border rounded-lg focus:ring-2 outline-none ${
                  errors.newPassword
                    ? "border-red-500 focus:ring-red-300"
                    : "border-yellow-300 focus:ring-yellow-300"
                }`}
              />
              {errors.newPassword && (
                <span className="text-red-500 text-sm mt-1">
                  {errors.newPassword}
                </span>
              )}
            </div>
 
            {/* Confirm Password */}
            <div className="flex flex-col">
              <input
                type="password"
                id="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={(e) =>
                  handleInputChange("confirmPassword", e.target.value)
                }
                className={`px-4 py-3 border rounded-lg focus:ring-2 outline-none ${
                  errors.confirmPassword
                    ? "border-red-500 focus:ring-red-300"
                    : "border-yellow-300 focus:ring-yellow-300"
                }`}
              />
              {errors.confirmPassword && (
                <span className="text-red-500 text-sm mt-1">
                  {errors.confirmPassword}
                </span>
              )}
            </div>
 
            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-3 bg-yellow-400 hover:bg-yellow-500 text-white font-semibold rounded-lg shadow transition-all disabled:opacity-50"
            >
              {isSubmitting ? "Updating..." : "Change Password"}
            </button>
 
            <div className="flex justify-center pt-4">
              <AnimatedLink onClick={handleBackToStore}>
                Return to Store
              </AnimatedLink>
            </div>
          </form>
        </div>
      </div>
 
      {/* Success Modal */}
      {showModal && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="bg-white rounded-2xl p-8 max-w-md w-full shadow-lg relative"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-yellow-100 mb-4">
                <svg
                  className="h-7 w-7 text-yellow-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-yellow-500 mb-2">
                Password Changed Successfully!
              </h2>
              <p className="text-gray-600 mb-6">
                Your password has been updated. You can now sign in with your
                new password.
              </p>
              <button
                onClick={closeModal}
                className="w-full px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-white rounded-lg transition-all"
              >
                Go to Login
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}
 
export default ChangePassword;